/**
 * Tiny safe markdown renderer for assistant replies.
 *
 * Why not a real library? markdown-it / react-markdown would add 30-60 KB
 * to a widget that should stay under 200 KB. The AI's output uses a tight
 * subset of markdown — bullets, numbered lists, bold, italics, inline
 * code, and the occasional link. This handles exactly that subset.
 *
 * Safety: every input string is HTML-escaped before formatting marks are
 * converted to JSX. We never use dangerouslySetInnerHTML. URL targets
 * are restricted to http(s)/mailto/tel so an injected `javascript:` URL
 * can't fire.
 */
import { Fragment, type ReactNode } from 'react';

const URL_REGEX = /\b(https?:\/\/[^\s<>()[\]"]+)/g;
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

/** Top-level: split into blocks (paragraphs / lists) and render each. */
export function renderMarkdown(input: string): ReactNode {
  const text = (input ?? '').replace(/\r\n/g, '\n').trim();
  if (text === '') return null;

  const blocks: ReactNode[] = [];
  const lines = text.split('\n');
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Bulleted list — group consecutive `* ` / `- ` / `• ` lines.
    if (/^\s*[*\-•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[*\-•]\s+/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\s*[*\-•]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={key++} className="g-md__list">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Numbered list — group consecutive `1.` / `2.` etc.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={key++} className="g-md__list g-md__list--ordered">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Paragraph — collect lines until blank line or next list start.
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !/^\s*[*\-•]\s+/.test(lines[i] ?? '') &&
      !/^\s*\d+\.\s+/.test(lines[i] ?? '')
    ) {
      paraLines.push(lines[i] ?? '');
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push(
        <p key={key++} className="g-md__p">
          {paraLines.map((pl, idx) => (
            <Fragment key={idx}>
              {idx > 0 && <br />}
              {renderInline(pl)}
            </Fragment>
          ))}
        </p>,
      );
    }
    // Skip blank lines.
    while (i < lines.length && (lines[i] ?? '').trim() === '') i++;
  }

  return <div className="g-md">{blocks}</div>;
}

/**
 * Inline formatting: bold (**x**), italic (*x* / _x_), inline code (`x`),
 * markdown links [text](url), and bare URLs auto-linked.
 */
function renderInline(text: string): ReactNode {
  // Strategy: tokenize on the formatting marks in order of precedence
  // (code first since it shouldn't be re-parsed for bold/italic), then
  // walk each token recursively for the lower-precedence marks.
  return tokenizeCode(text);
}

function tokenizeCode(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /`([^`\n]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<Fragment key={key++}>{tokenizeLink(text.slice(last, m.index))}</Fragment>);
    parts.push(<code key={key++} className="g-md__code">{m[1]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={key++}>{tokenizeLink(text.slice(last))}</Fragment>);
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function tokenizeLink(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+|tel:[^\s)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<Fragment key={key++}>{tokenizeBold(text.slice(last, m.index))}</Fragment>);
    const href = m[2] ?? '';
    if (SAFE_URL.test(href)) {
      parts.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="g-md__link">
          {m[1]}
        </a>,
      );
    } else {
      parts.push(<Fragment key={key++}>{m[1]}</Fragment>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={key++}>{tokenizeBold(text.slice(last))}</Fragment>);
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function tokenizeBold(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\*\*([^*\n]+)\*\*|__([^_\n]+)__/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<Fragment key={key++}>{tokenizeItalic(text.slice(last, m.index))}</Fragment>);
    parts.push(<strong key={key++}>{tokenizeItalic(m[1] ?? m[2] ?? '')}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={key++}>{tokenizeItalic(text.slice(last))}</Fragment>);
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function tokenizeItalic(text: string): ReactNode {
  // Single * surrounded by non-spaces. Avoid eating bullet-list markers
  // (handled at block level already) or double ** (handled above).
  const parts: ReactNode[] = [];
  const re = /(?<![\w*])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![\w*])|(?<![\w_])_(?!\s)([^_\n]+?)(?<!\s)_(?![\w_])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<Fragment key={key++}>{autoLink(text.slice(last, m.index))}</Fragment>);
    parts.push(<em key={key++}>{m[1] ?? m[2] ?? ''}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={key++}>{autoLink(text.slice(last))}</Fragment>);
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function autoLink(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > last) parts.push(<Fragment key={key++}>{text.slice(last, m.index)}</Fragment>);
    const href = m[1] ?? '';
    if (SAFE_URL.test(href)) {
      parts.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="g-md__link">
          {href}
        </a>,
      );
    } else {
      parts.push(<Fragment key={key++}>{href}</Fragment>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={key++}>{text.slice(last)}</Fragment>);
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}
